import * as THREE from 'three';

/**
 * Реестр инстанс-батчей массовой группы сущностей (панели/полки/фасады —
 * повторяющиеся пары (геометрия, материал)).
 *
 * ЗАЧЕМ ТАК, А НЕ ПРОСТО InstancedMesh. Контракт сцены держится на трёх вещах,
 * которые классический InstancedMesh не выражает напрямую:
 *
 *   1. raycast-выделение по сущности (instanceId → entityId);
 *   2. per-instance скрытие (тумблеры «Размеры/Фасады/X-Ray/скрытие» и слои
 *      Комната/Мебель/Техслой работают через `mesh.visible` по каждому
 *      объекту из entityIndex);
 *   3. анимации трансформов (перетаскивание модуля, открывание фасада) —
 *      существующие хуки двигают Object3D из entityIndex напрямую.
 *
 * Поэтому каждая инстансируемая сущность получает ПРОКСИ — настоящий
 * THREE.Object3D в графе сцены с userdata сущности:
 *
 *   - видимость: существующий код выставляет `proxy.visible`, тик реестра
 *     читает флаг и пишет нулевую матрицу в слот инстанса (невидимый
 *     инстанс вырожден — не рисуется и не попадает в raycast);
 *   - трансформы: прокси стоит на месте классического меша, существующий код
 *     двигает его как раньше; тик синхронизирует matrix → instanceMatrix;
 *   - рамки выделения/габариты: у прокси есть невидимый ребёнок-Mesh с той же
 *     общей геометрией — Box3.setFromObject и весь код рамок работает без
 *     изменений (ребёнок с visible=false не рисуется, в raycast приложения
 *     не попадает — фильтр по visible стоит в самом useSelection);
 *   - выделение: рёбра-оверлеи рисуются НЕ per-mesh, а одним слитым
 *     LineSegments на бакет (per-instance рёбра невозможны). При скрытии
 *     слота его блок рёбер схлопывается в точку под сценой — нулевой длины,
 *     не рисуется и не ловит луч.
 *
 * ТЕХРЕЖИМЫ (xray/wireframe/flat). Рентген делает материалы полупрозрачными
 * per-entity: прозрачные объекты three.js сортирует по объектам, а внутри
 * одного InstancedMesh сортировки инстансов нет. Поэтому в этих режимах
 * реестр ПОЛНОСТЬЮ SUSPEND'ится: каждая сущность получает классического
 * двойника (реальный меш + собственные рёбра) ребёнком своего прокси, слоты
 * обнуляются. Возврат в solid/photoreal — обратная сборка. Ключ бакета
 * включает финальные свойства материала (подкрашенные полки, emissive,
 * opacity), поэтому двойник визуально байт-в-байт совпадает с классическим
 * путём.
 *
 * РЕФСЧЁТ. Геометрии/материалы общие и живут в refcounter'е лоадера. Реестр
 * получает хуки retain/releaseObj (это retainSharedResources/
 * releaseSharedResources) и держит одну ссылку уровня бакета (меш + слитые
 * рёбра) и по одной на каждого «двойника» суспенда. Ссылки прокси-детей
 * (невидимые bounds-дети) учитывает сам лоадер — он и так делает
 * retain/releaseSharedResources на каждом объекте, который выдаёт
 * buildEntityObject.
 */

export type InstancedEdgeSpec = {
  hardware: boolean;
  glass: boolean;
  cylinder: boolean;
  /** Общая EdgesGeometry этой геометрии (кэш билдера, ключ `edge|${geometryKey}`). */
  edgeGeometry: THREE.BufferGeometry;
};

export type InstancedEntityRecipe = {
  /**
   * Ключ записи реестра. Для составных объектов (hardware-профиль из
   * нескольких частей) каждая часть регистрируется со своим ключом
   * `${entityId}::p${idx}` и общим ownerKey; матрицы частей-детей задаются
   * ЛОКАЛЬНО относительно прокси-владельца (только владелец добавляется в
   * граф, дети парентятся к нему вызывающим).
   */
  entityId: string;
  /** Каскадное освобождение: release(ownerKey) снимает все части объекта. */
  ownerKey?: string;
  /** Что вернуть из raycast-резолва по слоту (по умолчанию entityId). */
  pickId?: string;
  /** Общая кэшированная геометрия (то, что классический путь дал бы мешу). */
  geometry: THREE.BufferGeometry;
  geometryKey: string;
  /** Собственный клон материала этой сущности (как в классическом пути). */
  material: THREE.MeshPhysicalMaterial;
  /** Ключ бакета: базовый ключ материала + финальные свойства (тюнинг полок/emissive/opacity). */
  materialKey: string;
  /** Начальный локальный трансформ (относительно родителя прокси). */
  matrix: THREE.Matrix4;
  renderOrder: number;
  edge: InstancedEdgeSpec | null;
  initiallyVisible: boolean;
  /** Штамп userdata сущности — ровно тот, что классический путь ставит на меш. */
  userData: Record<string, unknown>;
};

export type InstancedBatchHooks = {
  /** retainSharedResources из useGltfLoader — для ресурсов уровня бакета и двойников. */
  retain: (obj: THREE.Object3D) => void;
  /** releaseSharedResources из useGltfLoader — освобождает учтённое выше. */
  releaseObj: (obj: THREE.Object3D) => void;
};

type Bucket = {
  key: string;
  geometry: THREE.BufferGeometry;
  geometryKey: string;
  material: THREE.MeshPhysicalMaterial;
  mesh: THREE.InstancedMesh;
  /** Слитые рёбра бакета (null, когда рёбра не запрошены). */
  edges: THREE.LineSegments | null;
  edgeVertsPerInstance: number;
  /** Исходные вершины EdgesGeometry — источник для записи блоков слотов. */
  edgeSourcePositions: Float32Array;
  capacity: number;
  used: number;
  /** slot → entityId; живые слоты идут подряд (компакция swap-with-last). */
  slotEntity: Array<string | null>;
  hidden: Uint8Array;
  dirty: boolean;
};

type InstancedRecord = {
  entityId: string;
  ownerKey: string | null;
  pickId: string;
  bucket: Bucket;
  slot: number;
  proxy: THREE.Object3D;
  recipe: InstancedEntityRecipe;
  /** Классический двойник суспенда (ребёнок прокси). */
  twin: THREE.Mesh | null;
  appliedHidden: boolean;
  appliedMatrix: THREE.Matrix4;
  /** Форс-перепись матрицы/рёбер слота на следующем тике. */
  dirtyAll: boolean;
};

const ZERO_MATRIX = new THREE.Matrix4().set(
  0, 0, 0, 0,
  0, 0, 0, 0,
  0, 0, 0, 0,
  0, 0, 0, 1,
);

// Точка схлопывания рёбер скрытых слотов — центр самой панели (последняя
// известная матрица слота). Уводить вершины «под сцену» нельзя: r166
// Box3.setFromObject обходит ВСЕХ детей независимо от visible и читает
// geometry.boundingBox — точка вне сцены раздувает бокс и уносит подгонку
// камеры на миллиметры-в-космос. Схлопнутый сегмент нулевой длины не рисуется
// и не ловит луч.
const collapsePoint = new THREE.Vector3();

function sameMatrix(a: THREE.Matrix4, b: THREE.Matrix4): boolean {
  for (let i = 0; i < 16; i++) {
    if (a.elements[i] !== b.elements[i]) return false;
  }
  return true;
}

/** Видимость по цепочке до корня реестра: скрытый владелец скрывает части. */
function isChainVisible(object: THREE.Object3D, root: THREE.Object3D): boolean {
  let node: THREE.Object3D | null = object;
  while (node && node !== root) {
    if (node.visible === false) return false;
    node = node.parent;
  }
  return true;
}

function nextCapacity(needed: number): number {
  let cap = 8;
  while (cap < needed) cap *= 2;
  return cap;
}

export class InstancedBatchRegistry {
  private readonly root: THREE.Object3D;
  private readonly hooks: InstancedBatchHooks;
  private readonly buckets = new Map<string, Bucket>();
  private readonly records = new Map<string, InstancedRecord>();
  private suspended = false;
  private disposed = false;

  constructor(root: THREE.Object3D, hooks: InstancedBatchHooks) {
    this.root = root;
    this.hooks = hooks;
  }

  get active(): boolean {
    return !this.suspended && !this.disposed;
  }

  get suspendedState(): boolean {
    return this.suspended;
  }

  has(entityId: string): boolean {
    return this.records.has(entityId);
  }

  /**
   * Зарегистрировать сущность и вернуть её прокси. Если сущность уже была
   * зарегистрирована (инкрементальная пересборка), старый слот освобождается
   * (без touch его ресурсов — их ведёт лоадер через releaseSharedResources).
   */
  register(recipe: InstancedEntityRecipe): THREE.Object3D {
    if (this.disposed) throw new Error('instancing registry disposed');
    if (recipe.ownerKey && this.records.has(recipe.ownerKey)) this.release(recipe.ownerKey);
    if (this.records.has(recipe.entityId)) this.release(recipe.entityId);
    const bucket = this.ensureBucket(recipe);
    const slot = bucket.used;
    if (bucket.used >= bucket.capacity) this.growBucket(bucket);
    bucket.used += 1;
    bucket.slotEntity[slot] = recipe.pickId ?? recipe.entityId;

    const proxy = new THREE.Object3D();
    recipe.matrix.decompose(proxy.position, proxy.quaternion, proxy.scale);
    proxy.userData = { ...recipe.userData };
    // Невидимый bounds-ребёнок: даёт Box3.setFromObject(proxy) правильный
    // габарит для рамок выделения/модулей, но не рисуется и не участвует в
    // material-режимах (analysisMarker — документированный флаг пропуска в
    // useRenderModes/furnitureRenderObject).
    const boundsChild = new THREE.Mesh(bucket.geometry, bucket.material);
    boundsChild.visible = false;
    boundsChild.castShadow = false;
    boundsChild.receiveShadow = false;
    boundsChild.userData.analysisMarker = true;
    boundsChild.userData.entityId = proxy.userData.entityId ?? recipe.pickId ?? recipe.entityId;
    boundsChild.userData.instancedBoundsFor = recipe.entityId;
    proxy.add(boundsChild);

    const record: InstancedRecord = {
      entityId: recipe.entityId,
      ownerKey: recipe.ownerKey ?? null,
      pickId: recipe.pickId ?? recipe.entityId,
      bucket,
      slot,
      proxy,
      recipe,
      twin: null,
      appliedHidden: !recipe.initiallyVisible,
      appliedMatrix: recipe.matrix.clone(),
      dirtyAll: true,
    };
    this.records.set(recipe.entityId, record);

    bucket.mesh.setMatrixAt(slot, recipe.initiallyVisible ? recipe.matrix : ZERO_MATRIX);
    bucket.mesh.instanceMatrix.needsUpdate = true;
    bucket.hidden[slot] = recipe.initiallyVisible ? 0 : 1;
    this.syncHiddenFlag(bucket, slot);
    this.writeEdgeBlock(bucket, slot, recipe.initiallyVisible ? recipe.matrix : null, recipe.matrix);
    this.syncSlotEntityIds(bucket);
    bucket.dirty = true;
    return proxy;
  }

  /**
   * Освободить слот сущности (для составных объектов — владельца: части
   * снимаются каскадом по ownerKey). Ресурсы прокси (bounds-детей)
   * освобождает вызывающий (лоадер) — здесь только слоты, компакция и уборка
   * пустых бакетов. Двойники суспенда убираются здесь же.
   */
  release(entityId: string): void {
    const record = this.records.get(entityId);
    const members = [...this.records.values()].filter((r) => r.ownerKey === entityId);
    if (!record && members.length === 0) return;
    for (const member of members) this.releaseSingle(member);
    if (record) this.releaseSingle(record);
  }

  private releaseSingle(record: InstancedRecord): void {
    if (!this.records.has(record.entityId)) return;
    this.records.delete(record.entityId);
    this.removeTwin(record);
    record.proxy.parent?.remove(record.proxy);

    const bucket = record.bucket;
    const last = bucket.used - 1;
    if (record.slot !== last) {
      // Компакция: последний занятый слот переезжает на место освобождённого.
      const movedId = bucket.slotEntity[last];
      const moved = movedId ? this.records.get(movedId) : null;
      bucket.slotEntity[record.slot] = movedId;
      if (moved) {
        moved.slot = record.slot;
        bucket.mesh.setMatrixAt(record.slot, moved.appliedHidden ? ZERO_MATRIX : moved.appliedMatrix);
        bucket.hidden[record.slot] = moved.appliedHidden ? 1 : 0;
        this.copyEdgeBlock(bucket, last, record.slot);
      }
    }
    bucket.slotEntity[last] = null;
    bucket.used = last;
    this.syncHiddenFlag(bucket, record.slot);
    this.syncSlotEntityIds(bucket);
    bucket.dirty = true;
    if (bucket.used === 0) this.disposeBucket(bucket);
  }

  /** Выселить сущность в классического двойника (ребёнок её прокси). */
  evict(entityId: string): void {
    const record = this.records.get(entityId);
    if (!record || record.twin) return;
    const { recipe } = record;
    const twin = new THREE.Mesh(recipe.geometry, recipe.material);
    twin.castShadow = true;
    twin.receiveShadow = true;
    if (recipe.renderOrder) twin.renderOrder = recipe.renderOrder;
    twin.userData = {
      ...recipe.userData,
      __instancingTwinOf: recipe.entityId,
    };
    if (recipe.edge) {
      // Точная копия attachBasisEdgeOverlay классического пути: свой материал
      // на каждого двойника, чтобы per-entity режимы применялись как раньше.
      const lines = new THREE.LineSegments(
        recipe.edge.edgeGeometry,
        new THREE.LineBasicMaterial({
          color: recipe.edge.hardware ? 0x6c7480 : recipe.edge.glass ? 0x334155 : 0x15181d,
          transparent: true,
          opacity: recipe.edge.hardware ? 0.38 : 0.62,
          depthTest: true,
          depthWrite: false,
          toneMapped: false,
        }),
      );
      lines.name = `${recipe.entityId}::basis_edges`;
      lines.renderOrder = (recipe.renderOrder || 0) + (recipe.edge.hardware ? 4 : 2);
      lines.userData.basisEdgeOverlay = true;
      lines.userData.edgeHardware = recipe.edge.hardware;
      lines.userData.edgeGlass = recipe.edge.glass;
      lines.userData.edgeCylinder = recipe.edge.cylinder;
      for (const [key, value] of Object.entries(recipe.userData)) {
        lines.userData[key] = value;
      }
      twin.add(lines);
    }
    record.proxy.add(twin);
    record.twin = twin;
    this.hooks.retain(twin);
  }

  /** Вернуть выселенную сущность в инстанс. */
  restore(entityId: string): void {
    const record = this.records.get(entityId);
    if (!record) return;
    this.removeTwin(record);
    record.dirtyAll = true;
    record.bucket.dirty = true;
  }

  private removeTwin(record: InstancedRecord): void {
    if (!record.twin) return;
    record.twin.parent?.remove(record.twin);
    this.hooks.releaseObj(record.twin);
    record.twin = null;
  }

  /**
   * Суспенд (xray/wireframe/flat): каждая сущность получает классического
   * двойника, слоты и слитые рёбра гасятся. Ресурсы бакетов остаются живыми
   * (ссылки уровня бакета), прокси остаются в графе и в entityIndex.
   */
  setSuspended(flag: boolean): void {
    if (this.disposed || this.suspended === flag) return;
    this.suspended = flag;
    if (flag) {
      for (const record of this.records.values()) this.evict(record.entityId);
      for (const bucket of this.buckets.values()) {
        bucket.mesh.visible = false;
        if (bucket.edges) bucket.edges.visible = false;
      }
    } else {
      for (const record of this.records.values()) this.restore(record.entityId);
      for (const bucket of this.buckets.values()) {
        bucket.mesh.visible = true;
        if (bucket.edges) bucket.edges.visible = true;
        bucket.dirty = true;
      }
    }
  }

  /**
   * Кадровая синхронизация: visible-флаги прокси (по цепочке родителей —
   * части-дети наследуют скрытие владельца) → нулевые матрицы; локальные
   * трансформы прокси (их двигают существующие анимации) → instanceMatrix.
   * Для прокси-детей локальная матрица домножается на цепочку родителей до
   * корня: записи идут в порядке регистрации, владелец обновляется раньше
   * своих частей.
   */
  tick(): boolean {
    if (this.disposed || this.suspended) return false;
    let changed = false;
    const world = new THREE.Matrix4();
    for (const record of this.records.values()) {
      const bucket = record.bucket;
      const hidden = !isChainVisible(record.proxy, this.root);
      if (hidden !== record.appliedHidden) {
        record.appliedHidden = hidden;
        bucket.hidden[record.slot] = hidden ? 1 : 0;
        this.syncHiddenFlag(bucket, record.slot);
        this.syncSlotEntityIds(bucket);
        record.dirtyAll = true;
      }
      if (hidden) {
        if (record.dirtyAll) {
          bucket.mesh.setMatrixAt(record.slot, ZERO_MATRIX);
          this.writeEdgeBlock(bucket, record.slot, null, record.appliedMatrix);
          record.dirtyAll = false;
          bucket.dirty = true;
          changed = true;
        }
        continue;
      }
      if (record.proxy.matrixAutoUpdate) record.proxy.updateMatrix();
      world.copy(record.proxy.matrix);
      let parent = record.proxy.parent;
      while (parent && parent !== this.root) {
        if (parent.matrixAutoUpdate) parent.updateMatrix();
        world.premultiply(parent.matrix);
        parent = parent.parent;
      }
      if (record.dirtyAll || !sameMatrix(world, record.appliedMatrix)) {
        record.appliedMatrix.copy(world);
        bucket.mesh.setMatrixAt(record.slot, world);
        this.writeEdgeBlock(bucket, record.slot, world);
        record.dirtyAll = false;
        bucket.dirty = true;
        changed = true;
      }
    }
    for (const bucket of this.buckets.values()) {
      if (!bucket.dirty) continue;
      bucket.dirty = false;
      bucket.mesh.instanceMatrix.needsUpdate = true;
      // Движенные инстансы могли выйти за сферу первого raycast'а — сброс
      // заставляет InstancedMesh пересчитать её перед следующим picking'ом.
      bucket.mesh.boundingSphere = null;
      if (bucket.edges) {
        (bucket.edges.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
        bucket.edges.geometry.computeBoundingSphere();
      }
    }
    return changed;
  }

  stats(): { buckets: number; instances: number; edgeBatches: number } {
    let instances = 0;
    let edgeBatches = 0;
    for (const bucket of this.buckets.values()) {
      instances += bucket.used;
      if (bucket.edges) edgeBatches += 1;
    }
    return { buckets: this.buckets.size, instances, edgeBatches };
  }

  /** Полная уборка при переключении модели. GPU-ресурсы освобождает
   * disposeObject по корню — здесь только внутренние карты. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.buckets.clear();
    this.records.clear();
  }

  // ------------------------------------------------------------------

  private ensureBucket(recipe: InstancedEntityRecipe): Bucket {
    const key = `${recipe.geometryKey}|${recipe.materialKey}|${recipe.renderOrder}`;
    const existing = this.buckets.get(key);
    if (existing) return existing;
    const capacity = nextCapacity(1);
    const geometry = recipe.geometry;
    const mesh = new THREE.InstancedMesh(geometry, recipe.material, capacity);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    mesh.userData.instanceEntityIds = [] as string[];
    mesh.userData.instanceHidden = new Uint8Array(capacity);
    mesh.userData.instancedBucket = key;
    if (recipe.renderOrder) mesh.renderOrder = recipe.renderOrder;

    let edges: THREE.LineSegments | null = null;
    let edgeVertsPerInstance = 0;
    let edgeSourcePositions = new Float32Array(0);
    if (recipe.edge) {
      const pos = recipe.edge.edgeGeometry.getAttribute('position') as THREE.BufferAttribute;
      edgeVertsPerInstance = pos ? pos.count : 0;
      edgeSourcePositions = (pos ? (pos.array as Float32Array) : new Float32Array(0)).slice();
      edges = new THREE.LineSegments(
        (() => {
          const buffer = new THREE.BufferGeometry();
          buffer.setAttribute(
            'position',
            new THREE.BufferAttribute(new Float32Array(capacity * edgeVertsPerInstance * 3), 3),
          );
          return buffer;
        })(),
        new THREE.LineBasicMaterial({
          // Покой (solid) — презентационные константы applyBasisEdgeOverlayMode:
          // техрежимы гасят весь инстансинг суспендом, поэтому другие ветки
          // не нужны (photoreal использует те же значения, что и solid).
          color: recipe.edge.hardware ? 0x4a443c : recipe.edge.glass ? 0x1f2937 : 0x574f43,
          transparent: true,
          opacity: recipe.edge.hardware ? 0.14 : recipe.edge.glass ? 0.3 : 0.16,
          depthTest: true,
          depthWrite: false,
          toneMapped: false,
        }),
      );
      edges.frustumCulled = false;
      if (recipe.renderOrder) edges.renderOrder = recipe.renderOrder + (recipe.edge.hardware ? 4 : 2);
      edges.userData.basisEdgeOverlay = true;
      edges.userData.edgeHardware = recipe.edge.hardware;
      edges.userData.edgeGlass = recipe.edge.glass;
      edges.userData.edgeCylinder = recipe.edge.cylinder;
      edges.userData.instancedBucketEdges = key;
    }

    const bucket: Bucket = {
      key,
      geometry,
      geometryKey: recipe.geometryKey,
      material: recipe.material,
      mesh,
      edges,
      edgeVertsPerInstance,
      edgeSourcePositions,
      capacity,
      used: 0,
      slotEntity: new Array<string | null>(capacity).fill(null),
      hidden: new Uint8Array(capacity),
      dirty: true,
    };
    this.root.add(mesh);
    if (edges) this.root.add(edges);
    this.hooks.retain(mesh);
    if (edges) this.hooks.retain(edges);
    this.buckets.set(key, bucket);
    return bucket;
  }

  private growBucket(bucket: Bucket): void {
    const capacity = bucket.capacity * 2;
    const mesh = new THREE.InstancedMesh(bucket.geometry, bucket.material, capacity);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    mesh.userData.instanceEntityIds = [];
    mesh.userData.instanceHidden = new Uint8Array(capacity);
    mesh.userData.instancedBucket = bucket.key;
    if (bucket.mesh.renderOrder) mesh.renderOrder = bucket.mesh.renderOrder;
    for (let i = 0; i < bucket.used; i++) {
      const id = bucket.slotEntity[i];
      const rec = id ? this.records.get(id) : null;
      mesh.setMatrixAt(i, rec && !rec.appliedHidden ? rec.appliedMatrix : ZERO_MATRIX);
      mesh.userData.instanceHidden[i] = bucket.hidden[i];
      if (rec) rec.dirtyAll = true;
    }
    mesh.instanceMatrix.needsUpdate = true;
    this.root.add(mesh);
    this.root.remove(bucket.mesh);
    this.hooks.retain(mesh);
    this.hooks.releaseObj(bucket.mesh);
    bucket.mesh = mesh;
    bucket.capacity = capacity;
    bucket.slotEntity.length = capacity;
    const hidden = new Uint8Array(capacity);
    hidden.set(bucket.hidden);
    bucket.hidden = hidden;

    if (bucket.edges && bucket.edgeVertsPerInstance) {
      const buffer = new THREE.BufferGeometry();
      const attr = new THREE.BufferAttribute(new Float32Array(capacity * bucket.edgeVertsPerInstance * 3), 3);
      const oldAttr = bucket.edges.geometry.getAttribute('position') as THREE.BufferAttribute;
      (attr.array as Float32Array).set(oldAttr.array as Float32Array);
      buffer.setAttribute('position', attr);
      const oldGeom = bucket.edges.geometry;
      bucket.edges.geometry = buffer;
      oldGeom.dispose();
      bucket.dirty = true;
    }
  }

  private syncHiddenFlag(bucket: Bucket, slot: number): void {
    (bucket.mesh.userData.instanceHidden as Uint8Array)[slot] = bucket.hidden[slot];
  }

  /** Убрать опустевший бакет. Ссылка уровня бакета освобождается через хуки:
   * общие геометрия/материал dispose'ит refcounter, когда умирает последняя
   * ссылка; собственные ресурсы слитых рёбер (буфер + материал) disposим
   * вручную — они в refcounter'е не учтены. */
  private disposeBucket(bucket: Bucket): void {
    this.buckets.delete(bucket.key);
    this.root.remove(bucket.mesh);
    this.hooks.releaseObj(bucket.mesh);
    if (bucket.edges) {
      this.root.remove(bucket.edges);
      this.hooks.releaseObj(bucket.edges);
      (bucket.edges.material as THREE.Material).dispose();
      bucket.edges.geometry.dispose();
    }
  }

  /** Пересобрать slot→entityId префикс для raycast-резолва. */
  private syncSlotEntityIds(bucket: Bucket): void {
    const out: string[] = [];
    for (let i = 0; i < bucket.used; i++) {
      const id = bucket.slotEntity[i];
      if (id) out.push(id);
    }
    bucket.mesh.userData.instanceEntityIds = out;
  }

  private copyEdgeBlock(bucket: Bucket, fromSlot: number, toSlot: number): void {
    if (!bucket.edges || !bucket.edgeVertsPerInstance) return;
    const attr = bucket.edges.geometry.getAttribute('position') as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    const verts = bucket.edgeVertsPerInstance;
    arr.copyWithin(toSlot * verts * 3, fromSlot * verts * 3, (fromSlot + 1) * verts * 3);
    bucket.dirty = true;
  }

  /**
   * Переписать блок рёбер слота. `matrix === null` — слот скрыт: вершины
   * схлопываются в центр панели (из `hiddenAt`) — сегменты нулевой длины не
   * рисуются, не ловят луч и не раздувают bounding box рёберного буфера.
   */
  private writeEdgeBlock(
    bucket: Bucket,
    slot: number,
    matrix: THREE.Matrix4 | null,
    hiddenAt?: THREE.Matrix4,
  ): void {
    if (!bucket.edges || !bucket.edgeVertsPerInstance) return;
    const src = bucket.edgeSourcePositions;
    const attr = bucket.edges.geometry.getAttribute('position') as THREE.BufferAttribute;
    const arr = attr.array as Float32Array;
    const verts = bucket.edgeVertsPerInstance;
    const base = slot * verts * 3;
    const v = new THREE.Vector3();
    if (hiddenAt) {
      collapsePoint.set(hiddenAt.elements[12], hiddenAt.elements[13], hiddenAt.elements[14]);
    }
    for (let i = 0; i < verts; i++) {
      if (matrix) {
        v.set(src[i * 3], src[i * 3 + 1], src[i * 3 + 2]).applyMatrix4(matrix);
      } else {
        v.copy(collapsePoint);
      }
      arr[base + i * 3] = v.x;
      arr[base + i * 3 + 1] = v.y;
      arr[base + i * 3 + 2] = v.z;
    }
    bucket.edges.geometry.setDrawRange(0, bucket.used * verts);
    bucket.dirty = true;
  }
}

/** Разрешить попадание raycast по InstancedMesh в entityId сущности. */
export function resolveInstancedPick(
  object: THREE.Object3D | null | undefined,
  instanceId: number | undefined | null,
): { entityId: string; hidden: boolean } | null {
  if (!object || typeof instanceId !== 'number') return null;
  if (!(object as THREE.InstancedMesh).isInstancedMesh) return null;
  const userData = object.userData as {
    instanceEntityIds?: string[];
    instanceHidden?: Uint8Array;
  } | null;
  if (!userData?.instanceEntityIds) return null;
  const entityId = userData.instanceEntityIds[instanceId];
  if (!entityId) return null;
  return { entityId, hidden: Boolean(userData.instanceHidden?.[instanceId]) };
}
